import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, from, shareReplay, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../auth/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export abstract class ApiService {
  private readonly getCache = new Map<string, Observable<unknown>>();

  protected constructor(
    protected http: HttpClient,
    protected authService: AuthService
  ) {}

  protected getHeaders(): Observable<HttpHeaders> {
    return from(this.authService.getToken()).pipe(
      map(
        (token) =>
          new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          })
      )
    );
  }

  // para no enviar content type y subir archivos
  public getHeadersFile(): Observable<HttpHeaders> {
    return from(this.authService.getToken()).pipe(
      map(
        (token) =>
          new HttpHeaders({
            Authorization: `Bearer ${token}`,
          })
      )
    );
  }

  protected handleError(error: any): Observable<never> {
    console.error(error);
    // Reenviar el HttpErrorResponse original para conservar status y body
    // (p. ej. { error: "Validation failed: ..." }) y poder mostrarlos en UI.
    return throwError(() => error);
  }

  protected get<T>(url: string): Observable<T> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.get<T>(url, { headers }).pipe(
          map((value) => value as T),
          catchError(this.handleError)
        )
      )
    );
  }

  protected getCached<T>(url: string): Observable<T> {
    const cached = this.getCache.get(url);
    if (cached) {
      return cached as Observable<T>;
    }

    const request$ = this.get<T>(url).pipe(
      tap({
        error: () => this.getCache.delete(url),
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.getCache.set(url, request$);
    return request$;
  }

  protected invalidateGetCache(urlPrefix?: string): void {
    if (!urlPrefix) {
      this.getCache.clear();
      return;
    }

    for (const key of [...this.getCache.keys()]) {
      if (key === urlPrefix || key.startsWith(`${urlPrefix}?`) || key.startsWith(`${urlPrefix}/`)) {
        this.getCache.delete(key);
      }
    }
  }

  protected post<T>(
    url: string,
    data: any,
    customHeaders: any = null,
  ): Observable<T> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const finalHeaders = customHeaders ? customHeaders : headers;
        return this.http.post<T>(url, data, { headers: finalHeaders }).pipe(
          map((value) => value as T),
          catchError(this.handleError)
        );
      })
    );
  }

  protected  postExcel(url: string, data: any): Observable<HttpResponse<any>> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const finalHeaders = headers
          .append('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
        return this.http.post(url, data, {
          headers: finalHeaders,
          responseType: 'blob',
          observe: 'response'
        }).pipe(catchError(this.handleError));
      })
    );
  }

  protected put<T>(url: string, data: any): Observable<T> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.put<T>(url, data, { headers }).pipe(
          map((value) => value as T),
          catchError(this.handleError)
        )
      )
    );
  }

  protected patch<T>(url: string, data: any): Observable<T> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.patch<T>(url, data, { headers }).pipe(
          map((value) => value as T),
          catchError(this.handleError)
        )
      )
    );
  }

  protected putFile<T>(url: string, data: any): Observable<T> {
    return this.getHeadersFile().pipe(
      switchMap((headers) =>
        this.http.put<T>(url, data, { headers }).pipe(
          map((value) => value as T),
          catchError(this.handleError)
        )
      )
    );
  }

  protected delete<T>(url: string): Observable<boolean> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http
          .delete<boolean>(url, { headers })
          .pipe(catchError(this.handleError))
      )
    );
  }
}
